/**
 * Types for the multi-programme reporting system.
 * @module reports/types
 */

import type { WorkflowState } from "@/server/types";
import type { Module, ModuleAssessment, PLO, Programme } from "@/types";

/* ------------------------------------------------------------------ */
/*  Programme Library                                                  */
/* ------------------------------------------------------------------ */

/** Default top-level institution name used when grouping programmes. Institution-neutral. */
export const DEFAULT_COLLEGE = "Unspecified Institution";

/**
 * What to call an NFQ level. Programmes with none group under 0, which is not
 * a level anybody awards, so it names itself the way the sibling dimensions do.
 */
export function nfqLevelLabel(level: number): string {
  return level ? `Level ${level}` : "No NFQ level";
}

/**
 * One governed programme projected into the report read-model.
 *
 * These records are derived from the API on every read and held in memory for
 * the lifetime of the reports scope. Nothing here is persisted in the browser —
 * the API and its SQL store are the source of truth.
 */
export interface StoredProgramme {
  /** Report-side identity. Always the underlying programme's own id. */
  libraryId: string;
  /** The full programme data object. */
  data: Programme;
  /** Organisational metadata derived from the programme and its tenant. */
  metadata: ProgrammeMetadata;
  /** ISO timestamp of the read this projection was built from. */
  syncedAt: string;
  /** Governance provenance. Every report record comes from the backend. */
  origin: BackendProgrammeOrigin;
}

export interface BackendProgrammeOrigin {
  kind: "backend";
  tenantId: string;
  revision: number;
  workflowState: WorkflowState;
  leadUserId?: string;
  leadUserName?: string;
  leadLastOpenedAt?: string | null;
}

export interface ProgrammeMetadata {
  /** Top-level institution name. Defaults to DEFAULT_COLLEGE. */
  college: string;
  /** School / department — taken from programme.discipline. */
  school: string;
  /** Optional academic year label, e.g. "2025/26". */
  academicYear?: string;
  /** Catalogue status: whether the institution offers the programme. */
  status?: ProgrammeStatus;
}

export type ProgrammeStatus = "active" | "dormant" | "in-validation";

/**
 * What each catalogue status reads as. Worded apart from the workflow state,
 * which the programmes list shows for the same record.
 */
export const CATALOGUE_STATUS_LABELS: Readonly<Record<ProgrammeStatus, string>> = {
  active: "Offered",
  "in-validation": "Not yet offered",
  dormant: "Withdrawn",
};

/** The label for a catalogue status token; an unknown token reads as itself. */
export function catalogueStatusLabel(status: string): string {
  return CATALOGUE_STATUS_LABELS[status as ProgrammeStatus] ?? status;
}

/* ------------------------------------------------------------------ */
/*  Indexed / cross-programme lookup types                             */
/* ------------------------------------------------------------------ */

/**
 * Cross-programme index built from all loaded StoredProgrammes.
 * Provides flat arrays for cross-cutting queries and grouped
 * maps for hierarchical reporting.
 */
export interface ProgrammeIndex {
  /** All stored programmes in the library. */
  programmes: StoredProgramme[];

  /* — Hierarchical groupings — */
  byCollege: Map<string, StoredProgramme[]>;
  bySchool: Map<string, StoredProgramme[]>;
  byNfqLevel: Map<number, StoredProgramme[]>;
  byAwardType: Map<string, StoredProgramme[]>;
  byStatus: Map<string, StoredProgramme[]>;
  byAcademicYear: Map<string, StoredProgramme[]>;

  /* — Flattened cross-cutting lookups — */
  allModules: IndexedModule[];
  allPLOs: IndexedPLO[];
  allAssessments: IndexedAssessment[];

  /** Modules grouped by code — reveals shared modules across programmes. */
  modulesByCode: Map<string, IndexedModule[]>;
}

/** A module enriched with its parent programme context. */
export interface IndexedModule {
  module: Module;
  programmeId: string;
  programmeTitle: string;
  school: string;
  nfqLevel: number | null;
  stageName: string;
  stageSequence: number;
  semester: number | null;
  isElective: boolean;
}

/** A PLO enriched with its parent programme context. */
export interface IndexedPLO {
  plo: PLO;
  programmeId: string;
  programmeTitle: string;
  school: string;
  nfqLevel: number | null;
  /** Number of MIMLOs mapped to this PLO across the programme. */
  mappedMimloCount: number;
}

/** An assessment enriched with its module and programme context. */
export interface IndexedAssessment {
  assessment: ModuleAssessment;
  moduleId: string;
  moduleTitle: string;
  moduleCode: string;
  moduleCredits: number;
  programmeId: string;
  programmeTitle: string;
  school: string;
  nfqLevel: number | null;
}

/* ------------------------------------------------------------------ */
/*  Filter state                                                       */
/* ------------------------------------------------------------------ */

/** Filter selections applied before running any report. */
export interface ReportFilters {
  colleges: string[];
  schools: string[];
  nfqLevels: number[];
  awardTypes: string[];
  academicYears: string[];
  statuses: ProgrammeStatus[];
}

/** Returns a ReportFilters with every array empty (= show all). */
export function emptyFilters(): ReportFilters {
  return {
    colleges: [],
    schools: [],
    nfqLevels: [],
    awardTypes: [],
    academicYears: [],
    statuses: [],
  };
}
